// RRSchedulePanel — RR Update 2 + 3 (Feb 2026).
//
// - Schedule Mode picker (Full RR / Limited Matches Per Team / Manual)
// - Generate button that respects current mode + gates on pool assignments
// - Auto-schedule (fill unscheduled only, or Rebuild with confirm)
// - Publish / Unpublish toggle for player visibility
// - Reset Schedule (blocked once results have been recorded; auto-unpublishes)
// - Warnings surface for team_double_booked / court_conflict /
//   duplicate_matchup / below_match_guarantee.
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { WarningCircle, Play, ArrowsClockwise, ListChecks, Calendar, EyeSlash, Eye } from '@phosphor-icons/react';
import { roundRobinAPI } from '../../lib/roundRobinApi';

const PRE_GEN_STATUSES = new Set(['draft', 'registration_open', 'registration_closed']);
const HAS_SCHEDULE_STATUSES = new Set(['schedule_generated', 'in_progress', 'completed']);

function reportError(err, fallback) {
  const d = err?.response?.data?.detail;
  const msg = typeof d === 'string' ? d : (d?.message || fallback);
  toast.error(msg);
}

const WARNING_CODE_LABELS = {
  team_double_booked:    'Team double-booked',
  court_conflict:        'Court conflict',
  duplicate_matchup:     'Duplicate matchup',
  below_match_guarantee: 'Below match guarantee',
};

function WarningsList({ warnings, entries }) {
  if (!warnings || warnings.length === 0) return null;
  const byId = Object.fromEntries((entries || []).map((e) => [e.id, e.display_name]));
  return (
    <div className="border border-amber-200 rounded-2xl bg-amber-50/60 p-3 space-y-2" data-testid="rr-schedule-warnings">
      <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold flex items-center gap-1">
        <WarningCircle size={12} weight="fill" /> Schedule warnings ({warnings.length})
      </div>
      <ul className="text-[12px] text-amber-900 space-y-1">
        {warnings.map((w, i) => (
          <li key={i} data-testid={`rr-warning-${w.code}-${i}`} className="flex gap-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-200/70 text-amber-900 text-[10px] font-semibold shrink-0">
              {WARNING_CODE_LABELS[w.code] || w.code}
            </span>
            <span className="flex-1">
              {w.message}
              {w.entry_id && byId[w.entry_id] ? <> · <span className="font-medium">{byId[w.entry_id]}</span></> : null}
              {w.entry_ids && w.entry_ids.length === 2 ? <> · <span className="font-medium">{byId[w.entry_ids[0]] || '—'} vs {byId[w.entry_ids[1]] || '—'}</span></> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RRSchedulePanel({ rr, onChanged }) {
  const [mode, setMode] = useState(rr.schedule_mode || 'full');
  const [target, setTarget] = useState(rr.matches_per_team || 2);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setMode(rr.schedule_mode || 'full');
    setTarget(rr.matches_per_team || 2);
  }, [rr.schedule_mode, rr.matches_per_team]);

  const preGen  = PRE_GEN_STATUSES.has(rr.status);
  const hasSched = HAS_SCHEDULE_STATUSES.has(rr.status);
  const started = rr.status === 'in_progress' || rr.status === 'completed';

  // Whether Event Day Settings are complete enough for the server-side
  // auto-scheduler to do its job. We chain generate → auto-schedule so a
  // single "Generate" click produces a full playable schedule (court +
  // time populated on every match) whenever the RR is fully configured.
  // If any setting is missing, we fall back to the previous two-step
  // flow (Generate produces bare matches, admin uses the Auto-schedule
  // button below once settings are complete).
  const eventDayReady = !!(
    (rr.court_labels && rr.court_labels.length > 0)
    && rr.match_duration_minutes
    && rr.play_day_start_time
    && rr.play_day_end_time
    && rr.starts_at
  );

  const generate = async (which) => {
    const chosenMode = which || mode;
    setBusy(true);
    try {
      const opts = { mode: chosenMode };
      if (chosenMode === 'limited') opts.matches_per_team = target;
      await roundRobinAPI.generateWith(rr.id, opts);
      // Chain auto-schedule immediately for full & limited when settings are
      // complete. This is the fix for the P0 where "Limited Number of
      // Matches" (and, latently, Full too) left every match without a
      // scheduled_at / court_label — the backend's generate endpoint has
      // never written those fields; only auto-schedule does. Manual mode
      // creates no matches so there's nothing to place.
      let chained = null;
      if (chosenMode !== 'manual' && eventDayReady) {
        try {
          const r = await roundRobinAPI.autoSchedule(rr.id, { rebuild: false });
          chained = r?.data?.auto_schedule_result || null;
        } catch (autoErr) {
          // Surface auto-schedule failures but do NOT undo the generated
          // matches — admin can still hand-place or fix settings and hit
          // the Auto-schedule button below.
          reportError(autoErr, 'Matches generated, but auto-schedule failed. Adjust courts / duration / window and click Auto-schedule.');
          onChanged?.();
          return;
        }
      }
      if (chosenMode === 'manual') {
        toast.success('Empty schedule ready — add matches below.');
      } else if (chained) {
        if (chained.unplaced?.length) {
          toast.warning(`Schedule generated. Placed ${chained.placed}. ${chained.unplaced.length} match${chained.unplaced.length === 1 ? '' : 'es'} could not fit — adjust courts, duration, or window.`);
        } else {
          toast.success(`Schedule generated — ${chained.placed} match${chained.placed === 1 ? '' : 'es'} placed with courts + times.`);
        }
      } else {
        toast.success('Schedule generated. Set courts and daily play window under Event Day Settings, then use Auto-schedule to place courts + times.');
      }
      onChanged?.();
    } catch (e) { reportError(e, 'Generate failed.'); }
    finally { setBusy(false); }
  };
  const doReset = async () => {
    if (!window.confirm('Reset the schedule? Every match will be deleted and the public schedule will be unpublished.')) return;
    setBusy(true);
    try {
      await roundRobinAPI.resetSchedule(rr.id);
      toast.success('Schedule reset. Reconfigure and regenerate when ready.');
      onChanged?.();
    } catch (e) { reportError(e, 'Reset failed.'); }
    finally { setBusy(false); }
  };
  const canAutoSchedule = hasSched && rr.status !== 'completed' && rr.status !== 'archived' && rr.status !== 'cancelled';
  const doAuto = async (rebuild = false) => {
    if (rebuild && !window.confirm('Rebuild the entire schedule? Every non-completed match will be re-slotted from scratch. Completed matches are preserved.')) return;
    setBusy(true);
    try {
      const r = await roundRobinAPI.autoSchedule(rr.id, { rebuild });
      const info = r?.data?.auto_schedule_result;
      if (info) {
        if (info.unplaced?.length) {
          toast.warning(`Placed ${info.placed}. ${info.unplaced.length} match${info.unplaced.length === 1 ? '' : 'es'} could not fit — adjust courts, duration, or window.`);
        } else {
          toast.success(`Placed ${info.placed} match${info.placed === 1 ? '' : 'es'}.`);
        }
      } else {
        toast.success('Auto-schedule complete.');
      }
      onChanged?.();
    } catch (e) { reportError(e, 'Auto-schedule failed.'); }
    finally { setBusy(false); }
  };
  const doPublish = async () => {
    setBusy(true);
    try { await roundRobinAPI.publish(rr.id); toast.success('Schedule published. Players can now see it.'); onChanged?.(); }
    catch (e) { reportError(e, 'Publish failed.'); }
    finally { setBusy(false); }
  };
  const doUnpublish = async () => {
    if (!window.confirm('Return the schedule to Draft? Players will no longer see it until you publish again.')) return;
    setBusy(true);
    try { await roundRobinAPI.unpublish(rr.id); toast.success('Schedule unpublished.'); onChanged?.(); }
    catch (e) { reportError(e, 'Unpublish failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-4" data-testid="rr-schedule-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold flex items-center gap-1">
            <ListChecks size={12} weight="duotone" /> Schedule
          </div>
          <div className="text-[11px] text-[#6a766e] mt-1">
            Pick how matches should be created, then generate. All modes stay editable until each match is completed.
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${rr.schedule_published ? 'bg-emerald-100 text-emerald-800' : 'bg-[#f3f1ec] text-[#6a766e]'}`} data-testid="rr-publish-state">
              {rr.schedule_published ? <><Eye size={10} /> Published</> : <><EyeSlash size={10} /> Draft</>}
            </span>
            {rr.schedule_published_at && (
              <span className="text-[10px] text-[#9aa39d]">since {new Date(rr.schedule_published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {hasSched && (
            rr.schedule_published
              ? <button type="button" onClick={doUnpublish} disabled={busy} data-testid="rr-unpublish" className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-[#013523] text-xs flex items-center gap-1"><EyeSlash size={12} /> Unpublish</button>
              : <button type="button" onClick={doPublish}   disabled={busy} data-testid="rr-publish"   className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs flex items-center gap-1"><Eye size={12} weight="fill" /> Publish schedule</button>
          )}
          {hasSched && rr.status !== 'in_progress' && rr.status !== 'completed' && (
            <button
              type="button" onClick={doReset} disabled={busy}
              data-testid="rr-reset-schedule"
              className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs flex items-center gap-1"
            ><ArrowsClockwise size={12} /> Reset schedule</button>
          )}
        </div>
      </div>

      {/* Mode picker — only interactive when pre-generation */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="rr-schedule-mode-picker">
        {[
          { v: 'full',    title: 'Full Round Robin', desc: 'Every team plays every other team in their pool.' },
          { v: 'limited', title: 'Limited Matches',  desc: 'Every team plays a fixed number of matches.' },
          { v: 'manual',  title: 'Manual Schedule',  desc: 'Start blank; you build every match by hand.' },
        ].map((opt) => {
          const active = mode === opt.v;
          return (
            <button
              key={opt.v} type="button"
              disabled={!preGen}
              onClick={() => setMode(opt.v)}
              data-testid={`rr-mode-${opt.v}`}
              className={`text-left px-3 py-2 rounded-xl border transition-colors ${active ? 'border-[#013523] bg-[#f6f4ef]' : 'border-[#e3dcd1] bg-white'} disabled:opacity-50`}
            >
              <div className="text-[13px] font-semibold text-[#013523]">{opt.title}</div>
              <div className="text-[11px] text-[#6a766e] mt-0.5">{opt.desc}</div>
            </button>
          );
        })}
      </div>

      {mode === 'limited' && preGen && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="rr-limited-controls">
          <label className="text-[11px] text-[#013523] font-medium">Matches per team</label>
          <input
            type="number" min={1} max={32} step={1}
            value={target}
            onChange={(e) => setTarget(parseInt(e.target.value, 10) || 1)}
            data-testid="rr-matches-per-team"
            className="w-20 px-2 py-1 border border-[#e3dcd1] rounded-md text-sm"
          />
          <div className="text-[11px] text-[#6a766e]">Every team gets exactly this many distinct opponents (subject to pool size).</div>
        </div>
      )}

      {preGen && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button" disabled={busy}
            onClick={() => generate(mode)}
            data-testid="rr-generate"
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1"
          ><Play size={12} weight="fill" /> Generate schedule</button>
          {mode === 'full' && (
            <span className="text-[11px] text-[#6a766e]">Auto Generate + Edit — every match is editable after generation.</span>
          )}
          {mode === 'manual' && (
            <span className="text-[11px] text-[#6a766e]">Manual — start blank and add matches below.</span>
          )}
        </div>
      )}

      {started && (
        <div className="text-[11px] text-[#6a766e] bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg px-3 py-2">
          The event has started. Structural schedule changes remain live-editable for each uncompleted match (court, time, round, teams). Completed matches are locked.
        </div>
      )}

      {canAutoSchedule && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#f0ebe1]" data-testid="rr-auto-schedule-controls">
          <button
            type="button" onClick={() => doAuto(false)} disabled={busy}
            data-testid="rr-auto-schedule"
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1"
          ><Calendar size={12} weight="duotone" /> Auto-schedule unscheduled</button>
          <button
            type="button" onClick={() => doAuto(true)} disabled={busy}
            data-testid="rr-auto-schedule-rebuild"
            className="px-3 py-1.5 rounded-full border border-[#013523] text-[#013523] text-xs flex items-center gap-1"
          ><ArrowsClockwise size={12} /> Rebuild all</button>
          <span className="text-[11px] text-[#6a766e]">Auto-schedule fills empty slots only. Rebuild replaces every non-completed match&apos;s slot.</span>
        </div>
      )}

      <WarningsList warnings={rr.schedule_warnings} entries={rr.entries} />
    </div>
  );
}
